import { Alignment, Checkbox, FormGroup, InputGroup, TextArea } from "@blueprintjs/core";
import { useFormikContext, useField } from "formik";
import { FormInput } from "src/models/UserInputs";
import UserInputChoice from "../runs/UserInputChoice";
import UserInputListCollector from "../runs/UserInputListCollector";
import UserInputReference from "../runs/UserInputReference";

export type CustomFormikComponentProps = {
    name: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
};

export type CustomFormikReferenceComponentProps = BaseProps & {
    companyId: string;
    tableRefName: string;
    required: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
};

type BaseProps = {
    name: string;
    key: string;
    label: string;
};

type HasRequiredProps = BaseProps & { required: boolean };
type StringProps = HasRequiredProps & { type: string };
type ChoiceProps = HasRequiredProps & { choices: string[] };
type ReferenceProps = HasRequiredProps & { companyId: string; tableRef: string };

export function StringField({ name, key, label, type, required }: StringProps) {
    const { setFieldValue } = useFormikContext<any>();
    const [field] = useField(name);
    return (
        <FormGroup
            key={`formgroupkey_${key}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
        >
            <InputGroup
                key={`inputgroupkey_${key}`}
                value={field.value}
                type={type === "maskedstring" ? "password" : "text"}
                onChange={e => setFieldValue(name, e.target.value)}
            />
        </FormGroup>
    );
}

export function TextAreaField({ name, key, label, required }: StringProps) {
    const { setFieldValue } = useFormikContext<any>();
    const [field] = useField(name);
    return (
        <FormGroup
            key={`formgroupkey_${key}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
        >
            <TextArea
                key={`textarea_${key}`}
                value={field.value}
                fill
                growVertically={true}
                large={true}
                onChange={e => setFieldValue(name, e.target.value)}
            />
        </FormGroup>
    );
}

export function CheckBoxField({ name, key, label }: BaseProps) {
    const { setFieldValue } = useFormikContext<any>();
    const [field] = useField(name);
    return (
        <Checkbox
            key={`checkbox_${key}`}
            inline
            alignIndicator={Alignment.LEFT}
            label={label}
            checked={field.value}
            onChange={() => setFieldValue(name, !field.value)}
        />
    );
}

export function ChoiceField({ name, key, label, required, choices }: ChoiceProps) {
    const { setFieldValue } = useFormikContext<any>();
    return (
        <FormGroup
            key={`formgroupkey_${key}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
        >
            <UserInputChoice name={name} setFieldValue={setFieldValue} choices={choices as string[]} />
        </FormGroup>
    );
}

// In some templates users have the ability to change the company for which to run the template
// presence of this field in the formik context.
// If this field is present ReferenceFields and ListCollectorFields will overwrite the default
// passed in company that is set on the template.

// todo: figure out how not to reload this field when it's value is changed
// since this field is itself a reference field
const companyField = "u_companySysId_dom";

export function ReferenceField({ name, key, label, required, companyId, tableRef }: ReferenceProps) {
    const { setFieldValue } = useFormikContext<any>();
    const [field] = useField(companyField);
    const idToUse = field.value ? field.value.value : companyId;

    return (
        <UserInputReference
            key={key}
            name={name}
            required={required}
            setFieldValue={setFieldValue}
            companyId={idToUse}
            tableRefName={tableRef}
            label={label}
        />
    );
}

export function ListCollectorField({ name, key, label, required, companyId, tableRef }: ReferenceProps) {
    const { setFieldValue } = useFormikContext<FormInput>();
    const [field] = useField(companyField);
    const idToUse = field.value ? field.value.value : companyId;
    return (
        <UserInputListCollector
            key={key}
            name={name}
            required={required}
            setFieldValue={setFieldValue}
            companyId={idToUse}
            tableRefName={tableRef}
            label={label}
        />
    );
}
